/**
 * The note composer's open state and its draft, held by a component that
 * outlives the composer.
 *
 * `TabPanel` unmounts the panel it is not showing, so anything `NotesPanel`
 * keeps in its own state dies on every tab switch. That cost two defects:
 *
 *  - a cancelled composer reopened on the way back, because the request to
 *    open it survived the unmount while the record of having handled it did
 *    not; and
 *  - a half-typed note was discarded without warning, because the draft lived
 *    in `NoteComposer`.
 *
 * Both are the same shape of mistake, so both get the same answer: the state
 * lives here, in a hook a surviving parent calls (`OpportunityDetail`,
 * `AccountDetail`), and `NotesPanel` renders it rather than owning it. A panel
 * with no owner falls back to its own copy, which is correct for a panel that
 * never unmounts.
 *
 * `revealRequest` stays a counter on purpose. Opening is idempotent — a second
 * press of an already-open composer must not toggle it shut — but *scrolling*
 * to it is what a second press is asking for, and only a value that changes
 * every time can express that.
 */

import { useCallback, useMemo, useState } from 'react';

export interface NoteDraft {
  body: string;
  type: string;
}

export const EMPTY_NOTE_DRAFT: NoteDraft = { body: '', type: 'general' };

export interface NoteComposerState {
  /** Whether the new-note composer is showing. Survives the panel unmounting. */
  open: boolean;
  /** What has been typed into it so far. Survives the panel unmounting. */
  draft: NoteDraft;
  /** Bumped by every open request, so an already-open composer is re-revealed. */
  revealRequest: number;
  /** `Add note`, from the header or from the panel's own button. */
  requestOpen: () => void;
  /** `Cancel`, or a successful save: close it and forget the draft. */
  dismiss: () => void;
  setDraft: (draft: NoteDraft) => void;
}

export function useNoteComposer(): NoteComposerState {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<NoteDraft>(EMPTY_NOTE_DRAFT);
  const [revealRequest, setRevealRequest] = useState(0);

  const requestOpen = useCallback((): void => {
    setOpen(true);
    setRevealRequest((request) => request + 1);
  }, []);

  const dismiss = useCallback((): void => {
    setOpen(false);
    setDraft(EMPTY_NOTE_DRAFT);
  }, []);

  return useMemo(
    () => ({ open, draft, revealRequest, requestOpen, dismiss, setDraft }),
    [open, draft, revealRequest, requestOpen, dismiss],
  );
}
